import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useInventory, useStorerooms } from '../hooks/useReceiving';

const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 3 });

function Inventory() {
  const [storeroomId, setStoreroomId] = useState('');
  const [q, setQ] = useState('');
  const [inStock, setInStock] = useState(true);

  const { data: storerooms } = useStorerooms();
  const { data, isLoading, isError } = useInventory({ storeroom_id: storeroomId, q, in_stock: inStock ? '1' : '' });
  const items = data || [];

  const totals = useMemo(() => {
    const list = data || [];
    const skus = new Set(list.map((i) => i.product_id));
    const units = list.reduce((t, i) => t + Number(i.qty_on_hand || 0), 0);
    return { skus: skus.size, units };
  }, [data]);

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock on Hand</h1>
          <p className="mt-1 text-sm text-gray-500">Live inventory per storeroom, driven by goods receiving. Your periodic stock counts reconcile against this.</p>
        </div>
        <p className="text-sm text-gray-400">{totals.skus} SKUs · {num(totals.units)} units{storeroomId ? '' : ' across all storerooms'}</p>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or name…" className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
        <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All storerooms</option>
          {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <label className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600">
          <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} /> In stock only
        </label>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Item code</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Storeroom</th><th className="px-4 py-2.5 text-right">On hand</th><th className="px-4 py-2.5">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>}
            {isError && <tr><td colSpan={6} className="px-4 py-6 text-center text-rose-500">Could not load inventory.</td></tr>}
            {!isLoading && !isError && items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No stock recorded yet — receive a delivery to bring items in.</td></tr>}
            {items.map((i) => (
              <tr key={i.id} className="hover:bg-brand-light/40">
                <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-gray-600">{i.item_code}</td>
                <td className="px-4 py-2.5 text-sm text-gray-800">{i.name}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-500">{i.category || '—'}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-600">{i.storeroom_name}</td>
                <td className={`whitespace-nowrap px-4 py-2.5 text-right tabular-nums font-semibold ${Number(i.qty_on_hand) < 0 ? 'text-rose-600' : 'text-gray-800'}`}>{num(i.qty_on_hand)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-400">{i.updated_at ? new Date(i.updated_at).toLocaleDateString('en-GB') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Inventory;
